import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { formatPrograms } from 'common/Forms/Utils';
import Tabs from 'common/Settings/Tabs';
import useFlags from '@/hooks/useFlags';
import AdminSettingsPanel from '../../admin/subscribers/Settings/AdminSettingsPanel';
import AIFeaturesSettings from '../../admin/subscribers/Settings/AIFeaturesSettings';
import IntegrationOptions from '../../shared/Settings/IntegrationOptions';
import BetaFeatures from '../../shared/Settings/BetaFeatures';
import { queryClient } from '../../../utils/ReactQuery';
import styles from './Settings.module.scss';
import MyAccount from './Forms/MyAccount';
import StudentFeatures from './Forms/StudentFeatures';
import Grades from './Forms/Grades';
import StudentAccess from './Forms/StudentAccess';
import ImportYourData from './Forms/ImportYourData';

export default class Settings extends Component {
  static propTypes = {
    adminSettings: PropTypes.object,
    aiFeaturesReadOnly: PropTypes.bool,
    assignmentsPath: PropTypes.string,
    canToggleAiGrading: PropTypes.bool,
    canToggleAiQuestionBuilder: PropTypes.bool,
    canToggleAiReadingLevels: PropTypes.bool,
    canvasConnectionData: PropTypes.shape({
      displayName: PropTypes.string,
      email: PropTypes.string,
      profilePictureUrl: PropTypes.string,
      user_canvas_id: PropTypes.string
    }).isRequired,
    canvasSubscriberConnected: PropTypes.bool,
    connectedToCanvas1_3: PropTypes.bool,
    connectedToSchoology1_3: PropTypes.bool,
    displayUnitContent: PropTypes.bool.isRequired,
    getAdminInfoPath: PropTypes.string.isRequired,
    googleConnectionData: PropTypes.shape({
      displayName: PropTypes.string,
      email: PropTypes.string,
      profilePictureUrl: PropTypes.string
    }).isRequired,
    importDataPath: PropTypes.string,
    initialActiveTab: PropTypes.string,
    initialSettings: PropTypes.shape({
      assignments_based_grading_opt_in: PropTypes.bool,
      default_home: PropTypes.string,
      grade_notebooks_by: PropTypes.string,
      notebook_possible_points: PropTypes.string,
      program_homepage: PropTypes.string,
      show_page_numbers: PropTypes.bool,
      show_percentages: PropTypes.bool,
      student_add_lock: PropTypes.bool,
      student_challenge_highlight: PropTypes.string,
      student_closed_book_assessments: PropTypes.bool,
      student_reading_level_a_enabled: PropTypes.bool,
      student_reading_level_b_enabled: PropTypes.bool,
      student_spanish_enabled: PropTypes.bool,
      student_view_assessment_results_off: PropTypes.bool,
      trends_highlight: PropTypes.string
    }).isRequired,
    initialStaffer: PropTypes.shape({
      email: PropTypes.string.isRequired,
      first_name: PropTypes.string.isRequired,
      from_auto_roster_district: PropTypes.bool.isRequired,
      has_strict_password_requirement: PropTypes.bool.isRequired,
      last_name: PropTypes.string.isRequired,
      username: PropTypes.string
    }).isRequired,
    isSysadmin: PropTypes.bool,
    narrationEnabled: PropTypes.bool,
    programHasClassroomActivity: PropTypes.bool.isRequired,
    programHasLessonOutline: PropTypes.bool.isRequired,
    programHasReadingChecks: PropTypes.bool.isRequired,
    programHasUnits: PropTypes.bool.isRequired,
    programHasVideoActivity: PropTypes.bool.isRequired,
    programHasVocabGames: PropTypes.bool.isRequired,
    programId: PropTypes.number,
    reviewGameSetting: PropTypes.bool.isRequired,
    sameSeriesProgramsCount: PropTypes.number,
    sameSeriesProgramsPath: PropTypes.string,
    schoolLevel: PropTypes.string,
    scienceProgram: PropTypes.bool,
    showAdminSettingsTab: PropTypes.bool,
    showAiFeaturesTab: PropTypes.bool,
    signUpCodesPath: PropTypes.string.isRequired,
    stafferPath: PropTypes.string.isRequired,
    streamEnabled: PropTypes.bool,
    studentsPath: PropTypes.string,
    subscriber: PropTypes.shape({
      ai_grading_enabled: PropTypes.bool.isRequired,
      ai_question_builder_enabled: PropTypes.bool.isRequired,
      ai_reading_levels_enabled: PropTypes.bool.isRequired,
      id: PropTypes.number.isRequired,
    }),
    subscriberContactAdminMessage: PropTypes.string,
    subscriberId: PropTypes.number.isRequired,
    subscriberName: PropTypes.string.isRequired,
    subscriberSettingsOnly: PropTypes.bool,
    transferPath: PropTypes.string.isRequired,
    updatePath: PropTypes.string.isRequired,
    userType: PropTypes.string.isRequired
  };

  static defaultProps = {
    adminSettings: null,
    aiFeaturesReadOnly: false,
    assignmentsPath: null,
    canToggleAiGrading: false,
    canToggleAiQuestionBuilder: false,
    canToggleAiReadingLevels: false,
    canvasSubscriberConnected: false,
    connectedToCanvas1_3: false,
    connectedToSchoology1_3: false,
    importDataPath: null,
    initialActiveTab: null,
    isSysadmin: false,
    narrationEnabled: null,
    programId: null,
    sameSeriesProgramsCount: 1,
    sameSeriesProgramsPath: null,
    scienceProgram: null,
    showAdminSettingsTab: false,
    showAiFeaturesTab: false,
    streamEnabled: null,
    subscriber: null,
    subscriberSettingsOnly: false,
  };

  constructor(props) {
    super(props);

    const defaultTab = this.props.showAdminSettingsTab ? 'admin_settings' : 'my_account';
    this.state = {
      activeTab: this.props.initialActiveTab || defaultTab,
      canvasConnectionData: this.props.canvasConnectionData,
      googleConnectionData: this.props.googleConnectionData,
      isAssignmentsBasedGradingActive: !!this.props.initialSettings.assignments_based_grading_opt_in,
      isTeacherPacedModeActive: !!useFlags().teacherPacedMode,
      settings: this.props.initialSettings,
      staffer: this.props.initialStaffer,
    };

    this.setActiveTab = this.setActiveTab.bind(this);
    this.updateState = this.updateState.bind(this);
  }

  setActiveTab(tab) {
    this.setState({ activeTab: tab });

    const url = new URL(window.location.href);
    url.searchParams.set('initial_active_tab', tab);
    window.history.replaceState({}, '', url);
  }

  updateState(newState) {
    this.setState(newState);
  }

  _isActive(tab) {
    return this.state.activeTab === tab;
  }

  _renderIntegrationsTab() {
    return (
      <IntegrationOptions
        active={this._isActive('integrations')}
        canvasConnectionData={this.state.canvasConnectionData}
        canvasSubscriberConnected={this.props.canvasSubscriberConnected}
        connectedToCanvas1_3={this.props.connectedToCanvas1_3}
        connectedToSchoology1_3={this.props.connectedToSchoology1_3}
        googleConnectionData={this.state.googleConnectionData}
        programId={this.props.programId}
        subscriberId={this.props.subscriberId}
        updateState={this.updateState}
        userType={this.props.userType}
      />
    );
  }

  getProgramOptions() {
    return axios
      .get(this.props.sameSeriesProgramsPath)
      .then(response => this.setState({ programOptions: formatPrograms(response.data.data) }))
      .catch(error => console.log(error));
  }

  formatTabs() {
    const adminSettingsTab = this.props.showAdminSettingsTab ?
      [{ key: 'admin_settings', label: 'Admin Settings' }] :
      [];
    const aiFeaturesTab = this.props.showAiFeaturesTab ?
      [{ key: 'ai_features', label: 'AI Features' }] :
      [];

    if (this.props.subscriberSettingsOnly) {
      return [...adminSettingsTab, ...aiFeaturesTab];
    }

    const hasPrefixTabs = adminSettingsTab.length > 0 || aiFeaturesTab.length > 0;
    const divider = hasPrefixTabs ?
      [{ divider: true, key: 'divider-1', label: '' }] :
      [];
    const defaultTabs = [
      ...adminSettingsTab,
      ...aiFeaturesTab,
      ...divider,
      { key: 'my_account', label: 'My Account' },
    ];
    const programTabs = [
      { key: 'student_features', label: 'Student Features' },
      { key: 'grades', label: 'Grades' },
      { key: 'student_access', label: 'Student Access' }
    ];
    const optInFeaturesTab = { key: 'beta_features', label: 'Opt-In Features' };
    const googleTabs = { key: 'integrations', label: 'Integrations' };

    let tabs = defaultTabs;

    if (this.props.programId !== null) {
      if (this.props.sameSeriesProgramsCount > 1) {
        const importYourDataTab = { key: 'import_your_data', label: 'Import Your Data' };
        programTabs.splice(2, 0, importYourDataTab);
      }
      tabs = tabs.concat(programTabs);
    }

    tabs = [...tabs, optInFeaturesTab, googleTabs];

    return tabs;
  }

  render() {
    const { activeTab } = this.state;
    const { assignmentsBasedGrading } = useFlags();
    return (
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <div className={`dsp-flex ${styles.settings_container}`}>
          <Tabs
            activeTab={activeTab}
            setActiveTab={this.setActiveTab}
            tabs={this.formatTabs()}
          />

          <div className={styles.contentContainer}>
            {this.props.showAdminSettingsTab && this.props.adminSettings && (
              <AdminSettingsPanel
                active={this._isActive('admin_settings')}
                {...this.props.adminSettings}
              />
            )}
            {this.props.showAiFeaturesTab && this.props.subscriber && (
              <AIFeaturesSettings
                active={this._isActive('ai_features')}
                canToggleGrading={this.props.canToggleAiGrading}
                canToggleQuestionBuilder={this.props.canToggleAiQuestionBuilder}
                canToggleReadingLevels={this.props.canToggleAiReadingLevels}
                isSysadmin={this.props.isSysadmin}
                readOnly={this.props.aiFeaturesReadOnly}
                subscriber={this.props.subscriber}
              />
            )}
            {!this.props.subscriberSettingsOnly && (
              <>
                <MyAccount
                  active={this._isActive('my_account')}
                  getAdminInfoPath={this.props.getAdminInfoPath}
                  settings={this.state.settings}
                  signUpCodesPath={this.props.signUpCodesPath}
                  staffer={this.state.staffer}
                  subscriberContactAdminMessage={this.props.subscriberContactAdminMessage}
                  subscriberName={this.props.subscriberName}
                  transferPath={this.props.transferPath}
                  updatePath={this.props.updatePath}
                  updateState={this.updateState}
                  userType={this.props.userType}
                />
                <StudentFeatures
                  active={this._isActive('student_features')}
                  narrationEnabled={this.props.narrationEnabled}
                  programHasClassroomActivity={this.props.programHasClassroomActivity}
                  programHasLessonOutline={this.props.programHasLessonOutline}
                  programHasVideoActivity={this.props.programHasVideoActivity}
                  programHasVocabGames={this.props.programHasVocabGames}
                  reviewGameSetting={this.props.reviewGameSetting}
                  scienceProgram={this.props.scienceProgram}
                  settings={this.state.settings}
                  stafferPath={this.props.stafferPath}
                  streamEnabled={this.props.streamEnabled}
                  studentsPath={this.props.studentsPath}
                  updateState={this.updateState}
                />

                <Grades
                  active={this._isActive('grades')}
                  isAssignmentsBasedGradingActive={this.state.isAssignmentsBasedGradingActive}
                  settings={this.state.settings}
                  stafferPath={this.props.stafferPath}
                  updateState={this.updateState}
                />

                {this.props.sameSeriesProgramsCount > 1 && (
                  <ImportYourData
                    active={this._isActive('import_your_data')}
                    importDataPath={this.props.importDataPath}
                    programId={this.props.programId}
                    programsPath={this.props.sameSeriesProgramsPath}
                  />
                )}

                <BetaFeatures
                  active={this._isActive('beta_features')}
                  displayAssignmentsBasedGradingOptIn={assignmentsBasedGrading}
                  isAssignmentsBasedGradingActive={this.state.isAssignmentsBasedGradingActive}
                  isTeacherPacedModeActive={this.state.isTeacherPacedModeActive}
                  setIsAssignmentsBasedGradingActive={nextState => this.setState(
                    prev => ({ ...prev, isAssignmentsBasedGradingActive: nextState })
                  )}
                  setIsTeacherPacedModeActive={nextState => this.setState(
                    prev => ({ ...prev, isTeacherPacedModeActive: nextState })
                  )}
                  stafferPath={this.props.stafferPath}
                />

                <StudentAccess
                  active={this._isActive('student_access')}
                  assignmentsPath={this.props.assignmentsPath}
                  displayUnitContent={this.props.displayUnitContent}
                  programHasReadingChecks={this.props.programHasReadingChecks}
                  schoolLevel={this.props.schoolLevel}
                  scienceProgram={this.props.scienceProgram}
                  settings={this.state.settings}
                  stafferPath={this.props.stafferPath}
                  streamEnabled={this.props.streamEnabled}
                  updateState={this.updateState}
                />

                {this._renderIntegrationsTab()}
              </>
            )}
          </div>
        </div>
      </QueryClientProvider>
    );
  }
}
